import React, {
  useState, useCallback, useMemo, Fragment,
} from 'react';
import classnames from 'classnames';
import {
  ITextField,
  IRefObject,
  ActionButton,
  Dialog,
  TextField,
  DialogType,
  PrimaryButton,
  Separator,
  Stack,
} from '@fluentui/react';
import { Chat } from '@fluentui/react-northstar';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { createUseStyles } from 'react-jss';
import { useLogger } from '../Logger';
import { ChatMessage, messageProps } from './message-types';
import {
  LikeIcon, DislikeIcon, LikeThumb, DislikeThumb,
} from '../../Assets';
import videoMapping from '../../Data/videoMapping.json';
import { useIsVivara } from '../../hooks/useIsVivara';
import { useIsDevMode } from '../../hooks/useIsDevMode';
import { vivaraMessageStyle } from './common-styles';
import { PlaygroundTheme } from '../../Theme';

// eslint-disable-next-line max-len
export const getCustomTimestamp = (createdOnTime: Date) => createdOnTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric' }).replace(/PM/g, 'p.m.').replace(/AM/g, 'a.m.');

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  root: {
    '& .buttonGroup': {
      marginTop: '30px',
      marginBottom: '-30px',
    },
    '&.is-vivara': {
      '& .ms-Button': {
        backgroundColor: theme.themePrimary,
        borderColor: theme.themePrimary,
        '&:hover': {
          backgroundColor: theme.themeDarkAlt,
          borderColor: theme.themeDarkAlt,
        },
      },
      '& .ms-TextField-fieldGroup::after,.ms-Dropdown::after': {
        borderColor: theme.themePrimary,
      },
    },
  },
  message: {
    '&.is-vivara': {
      ...vivaraMessageStyle(theme),
    },
  },
  menuButtonBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: 12,
    '& button:hover': {
      color: theme.themePrimary,
    },
    '& .annotationButton': {
      height: 25,
      '& .ms-Icon': {
        fontSize: 14,
        marginTop: -2,
      },
    },
  },
}));

export const MessageWithActions: React.FunctionComponent<messageProps> = (props) => {
  const {
    message,
    messageContent,
    customClassName,
    onLike: onLikeHandler,
    onDislike: onDislikeHandler,
    disableLike,
    disableDislike,
    isShowDeploymentName,
    enableAnnotation,
    onAnnotation,
    isUserMessage,
  } = props;
  const logger = useLogger();

  const [hovered, setHovered] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [annotationTextValue, setAnnotationTextValue] = useState(message.annotation || '');
  const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
  const isVivara = useIsVivara();
  const isDevMode = useIsDevMode();
  const annotationTextFieldRef = React.useRef<ITextField>();

  const classes = useStyles();
  const className = classnames(customClassName, isVivara && 'is-vivara', isUserMessage && 'is-user-message', classes.message);

  const dialogContentProps = {
    type: DialogType.normal,
    showCloseButton: true,
    closeButtonAriaLabel: 'Close',
  };

  const modalProps = {
    isBlocking: true,
  };

  const onLike = useCallback(() => {
    if (liked) {
      logger.logInfo(`${message.messageId}: Like button cancelled`);
    } else {
      logger.logInfo(`${message.messageId}: Like button clicked`);
      if (disliked) logger.logInfo(`${message.messageId}: Dislike button cancelled`);
    }
    setLiked(!liked);
    setDisliked(false);
    setHovered(false);

    if (onLikeHandler) {
      onLikeHandler(message as ChatMessage, !liked);
    }
  }, [liked, disliked, logger, message, onLikeHandler]);

  const onDislike = useCallback(() => {
    if (disliked) {
      logger.logInfo(`${message.messageId}: Dislike button cancelled`);
    } else {
      logger.logInfo(`${message.messageId}: Dislike button clicked`);
      if (liked) logger.logInfo(`${message.messageId}: Like button cancelled`);
    }
    setDisliked(!disliked);
    setLiked(false);
    setHovered(false);

    if (onDislikeHandler) {
      onDislikeHandler(message as ChatMessage, !disliked);
    }
  }, [liked, disliked, logger, message, onDislikeHandler]);

  const onAnnotationButtonClick = useCallback(() => {
    setHovered(false);
    setShowAnnotationDialog(true);
  }, []);

  const onSaveAnnotation = useCallback(() => {
    const value = annotationTextFieldRef.current?.value || '';
    setAnnotationTextValue(value);
    if (onAnnotation) {
      onAnnotation(message as ChatMessage, value);
    }
    setShowAnnotationDialog(false);
  }, [message, onAnnotation]);

  const likedIcon = isVivara ? LikeIcon(true) : LikeThumb();
  const dislikedIcon = isVivara ? DislikeIcon(true) : DislikeThumb();

  const likeBtn = (
    <ActionButton
      disabled={disableLike}
      className="likeButton menuButton"
      onClick={onLike}
      title="Like"
    >
      {liked ? likedIcon : LikeIcon(liked)}
    </ActionButton>
  );
  const dislikeBtn = (
    <ActionButton
      disabled={disableDislike}
      className="dislikeButton menuButton"
      onClick={onDislike}
      title="Dislike"
    >
      {disliked ? dislikedIcon : DislikeIcon(disliked)}
    </ActionButton>
  );
  const annotationBtn = (
    <ActionButton
      className="annotationButton menuButton"
      onClick={onAnnotationButtonClick}
      title={annotationTextValue || 'Add notes'}
    >
      <FontIcon aria-label="Add notes" iconName="AddNotes" />
    </ActionButton>
  );

  const actionMenu = {
    iconOnly: true,
    items: [
      {
        key: 'like',
        icon: likeBtn,
        title: 'Like',
      },
      {
        key: 'dislike',
        icon: dislikeBtn,
        title: 'Dislike',
      },
      ...enableAnnotation ? [
        {
          key: 'separator',
          icon: <Separator vertical />,
        },
        {
          key: 'annotation',
          icon: annotationBtn,
          title: annotationTextValue || 'Add notes',
        }] : [],
    ].filter((item) => item != null),
  };

  const actionResult = (liked || disliked)
    && (
    <div className={hovered ? 'feedback-show-container hovered' : 'feedback-show-container'}>
      {liked
        ? (
          <ActionButton
            className="likeButton"
            onClick={onLike}
          >
            {LikeThumb()}
          </ActionButton>
        )
        : (
          <ActionButton
            className="dislikeButton"
            onClick={onDislike}
          >
            {DislikeThumb()}
          </ActionButton>
        )}
    </div>
    );

  const videoContent = useMemo(() => {
    const videoLinkRegex = /https:\/\/www\.youtube\.com\/watch\?v=[A-Za-z0-9_-]+(&t=[0-9]+[ms]?)?/;
    const videoIdMatch = message && message.content && message.content.match(videoLinkRegex);
    return videoIdMatch ? videoMapping.find((item: any) => item.videoLink === videoIdMatch[0]) : null;
  }, [message]);

  return (
    <>
      <Chat.Message
        content={{
          content: (
            <div>
              {isShowDeploymentName && message.deploymentName && (
              <div className="deployment-name">
                [Deployment Name:
                {message.deploymentName}
                ]
              </div>
              )}
              {messageContent}
              {isShowDeploymentName && annotationTextValue && (
              <div style={{ fontStyle: 'italic' }}>
                (Note:
                {annotationTextValue}
                )
              </div>
              )}
              {videoContent && videoContent.videoEmbedLink && (
                <div className="video-content-container">
                  <iframe
                    className="video-content"
                    src={videoContent.videoEmbedLink}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              )}
              {!isVivara && actionResult}
              {isVivara && !isUserMessage && (
              <div className={classes.menuButtonBar}>
                {likeBtn}
                {dislikeBtn}
                {isDevMode && annotationBtn}
              </div>
              )}
            </div>
          ),
        }}
        actionMenu={hovered && !isVivara ? actionMenu : null}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        timestamp={!isVivara && getCustomTimestamp(message.createdOn)}
        className={className}
      />
      <Dialog
        hidden={!showAnnotationDialog}
        onDismiss={() => {
          setShowAnnotationDialog(false);
        }}
        minWidth={800}
        dialogContentProps={{
          ...dialogContentProps,
          title: (
            <div className={classnames(classes.root, isVivara && 'is-vivara')}>
              <TextField
                label="Annotation"
                defaultValue={annotationTextValue}
                componentRef={annotationTextFieldRef as IRefObject<ITextField>}
                placeholder="Please input your comments."
                multiline
                style={{ height: '150px' }}
              />
              <Stack horizontal className="buttonGroup">
                <PrimaryButton onClick={onSaveAnnotation}>Save</PrimaryButton>
              </Stack>
            </div>
          ),
        }}
        modalProps={modalProps}
      />
    </>
  );
};
